<script setup lang="ts">
import { config } from '@/config'
import { useAuthStore } from '@/stores/auth'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { computed, ref } from 'vue'
import { getAvailableLanguages } from '@/i18n'
import { usePermissions } from '@/composables/usePermissions'
import ChangePasswordModal from '@/components/ChangePasswordModal.vue'
import ProfileUpdateModal from '@/components/ProfileUpdateModal.vue'
import PasskeyManagerModal from '@/components/PasskeyManagerModal.vue'
import { useLicensePurchaseAccess } from '@/composables/useLicensePurchaseAccess'

const { logout } = defineProps<{
  logout: () => void
}>()

const route = useRoute()
const authStore = useAuthStore()
const { t, locale } = useI18n()
const userName = computed(() => authStore.user?.name || 'Admin User')
const userEmail = computed(() => authStore.user?.email || 'admin@company.com')
const showLanguageMenu = ref(false)
const showUserMenu = ref(false)
const userMenuDropdown = ref<HTMLElement | null>(null)
const availableLanguages = getAvailableLanguages()
const { can } = usePermissions()
const { canPurchaseLicense } = useLicensePurchaseAccess()
const currentLanguageName = computed(() => {
  const lang = availableLanguages.find((l) => l.code === locale.value)
  return lang?.name || 'English'
})

const isPickupMonitor = computed(() => authStore.user?.role === 'pickup_monitor')

const navItems = computed(() => {
  // Hide navigation items for pickup_monitor users
  if (isPickupMonitor.value) {
    return []
  }

  const licensePermission = can('licenses:list') || authStore.user?.role === 'super_admin'
  // Admins included now: they get an overview of every connection they look
  // after, so the permission alone is the right gate. Company and partner
  // logins also need this to reach Settings > Company Details / Tax Details,
  // which aren't gated on the DATEV permission.
  // Without DATEV online the DATEV section holds only the export settings.
  const showSettingsNavItem =
    (can('company-datev:view') && (config.features.datevOnline || can('restaurant-datev-export:view'))) ||
    authStore.user?.role === 'restaurant_company' ||
    authStore.user?.role === 'partner'
  const showLicenseNavItem = licensePermission && (authStore.user?.role === 'super_admin' || canPurchaseLicense.value)

  const items = [
    { path: '/', label: t('nav.dashboard'), icon: 'dashboard', permission: true },
    { path: '/restaurants', label: t('nav.restaurants'), icon: 'building', permission: can('restaurant:list') },
    { path: '/partners', label: t('nav.partners'), icon: 'users', permission: can('partners:list') },
    { path: '/companies', label: t('nav.companies'), icon: 'building', permission: can('companies:list') },
    { path: '/users', label: t('nav.users'), icon: 'users', permission: can('users:list') },
    { path: '/roles-permissions', label: t('nav.rolesPermissions'), icon: 'lock', permission: can('role:list') },
    { path: authStore.user?.role === 'super_admin' ? '/licenses/reports' : '/licenses', label: t('nav.licenses'), icon: 'credit-card', permission: showLicenseNavItem },
    { path: '/settings', label: t('nav.settings'), icon: 'settings', permission: showSettingsNavItem },
  ]

  // A Datev Accountant sees Settings (where the DATEV export is) and nothing else.
  if (authStore.isDatevAccountant) {
    return items.filter((item) => item.permission && item.path === '/settings')
  }

  return items.filter((item) => item.permission)
})

const isActive = (path: string) => {
  if (path === '/') return route.path === '/'
  return route.path.startsWith(path)
}

const changeLanguage = async (lang: string) => {
  if (lang === locale.value) {
    showLanguageMenu.value = false
    return
  }

  const result = await authStore.updateLanguage(lang)
  if (result.success) {
    showLanguageMenu.value = false
  } else {
    console.error('Failed to update language:', result.message)
  }
}

const showChangePasswordModal = ref(false)
const showProfileUpdateModal = ref(false)
const showPasskeyManagerModal = ref(false)

const handleLogout = () => {
  showUserMenu.value = false
  logout()
}

const handleChangePasswordClick = () => {
  showChangePasswordModal.value = true
  showUserMenu.value = false
}

const handleProfileUpdateClick = () => {
  showProfileUpdateModal.value = true
  showUserMenu.value = false
}

const handlePasskeyClick = () => {
  showPasskeyManagerModal.value = true
  showUserMenu.value = false
}

const closeUserMenuOnLeave = () => {
  showUserMenu.value = false
}
</script>

<template>
  <nav class="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
    <div class="px-8 h-16 flex items-center justify-between gap-8">
      <!-- Left: Logo/Brand -->
      <div class="flex items-center gap-3 flex-shrink-0 min-w-0">
        <img
          src="/Flixorder_Web_icon-300x300.png"
          alt="Flixorder"
          class="h-8 w-8 rounded"
        />
        <span class="text-lg font-bold text-gray-900">Flixorder</span>
      </div>

      <!-- Center: Navigation Items -->
      <div class="hidden lg:flex items-center gap-0 flex-1 justify-center h-full">
        <RouterLink
          v-for="item in navItems"
          :key="item.path"
          :to="item.path"
          class="px-4 h-full flex items-center text-sm font-medium transition-all duration-200 border-b-2 relative group"
          :class="isActive(item.path)
            ? 'text-gray-900 border-b-primary-600'
            : 'text-gray-600 border-b-transparent hover:text-gray-900 hover:border-b-gray-300'
          "
        >
          {{ item.label }}
        </RouterLink>
      </div>

      <!-- Right: Controls -->
      <div class="flex items-center gap-4 flex-shrink-0 h-full">
        <!-- Language Selector -->
        <div class="relative">
          <button
            @click="showLanguageMenu = !showLanguageMenu"
            class="px-3 py-2 text-gray-600 hover:text-gray-900 transition-colors rounded-lg h-full flex items-center font-medium text-sm"
          >
            {{ locale.toUpperCase() }}
          </button>

          <div
            v-if="showLanguageMenu"
            class="absolute right-0 mt-0 w-40 bg-white rounded-lg shadow-lg border border-gray-200 z-50"
          >
            <button
              v-for="lang in availableLanguages"
              :key="lang.code"
              @click="changeLanguage(lang.code)"
              :class="[
                'w-full text-left px-4 py-2 text-sm transition-colors',
                locale === lang.code
                  ? 'bg-primary-50 text-primary-600 font-medium'
                  : 'text-gray-700 hover:bg-gray-50',
              ]"
            >
              {{ lang.name }}
            </button>
          </div>
        </div>

        <!-- Divider -->
        <div class="h-6 w-px bg-gray-200"></div>

        <!-- User Menu Dropdown -->
        <div
          ref="userMenuDropdown"
          class="relative h-full"
          @mouseleave="closeUserMenuOnLeave"
        >
          <button
            @click="showUserMenu = !showUserMenu"
            class="flex items-center gap-3 px-3 h-full transition-all duration-200"
          >
            <div class="flex flex-col items-end text-right">
              <span class="text-sm font-medium text-gray-900">{{ userName }}</span>
              <span class="text-xs text-gray-500">{{ userEmail }}</span>
            </div>
            <div class="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
              {{ userName.split(' ').map(n => n.charAt(0)).join('').toUpperCase() }}
            </div>
          </button>

          <div
            v-if="showUserMenu"
            class="absolute right-0 mt-0 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-50"
          >
            <div class="px-4 py-3 border-b border-gray-200">
              <p class="text-sm font-medium text-gray-900">{{ userName }}</p>
              <p class="text-xs text-gray-500">{{ userEmail }}</p>
            </div>
            <button
              @click="handleProfileUpdateClick"
              class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-600 transition-colors flex items-center gap-2"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {{ t('nav.updateProfile') }}
            </button>
            <button
              @click="handleChangePasswordClick"
              class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-600 transition-colors flex items-center gap-2"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              {{ t('nav.changePassword') }}
            </button>
            <button
              v-if="authStore.isManagementRole"
              @click="handlePasskeyClick"
              class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-600 transition-colors flex items-center gap-2"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.75 1.5a6.75 6.75 0 0 0-6.651 7.906c.067.39-.032.717-.221.906l-6.5 6.499a3 3 0 0 0-.878 2.121v2.818c0 .414.336.75.75.75H6a.75.75 0 0 0 .75-.75v-1.5h1.5A.75.75 0 0 0 9 19.5V18h1.5a.75.75 0 0 0 .53-.22l2.658-2.658c.19-.189.517-.288.906-.22A6.75 6.75 0 1 0 15.75 1.5Zm0 3a.75.75 0 0 0 0 1.5A2.25 2.25 0 0 1 18 8.25a.75.75 0 0 0 1.5 0 3.75 3.75 0 0 0-3.75-3.75Z" />
              </svg>
              {{ t('nav.managePasskeys') }}
            </button>
            <button
              @click="handleLogout"
              class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors flex items-center gap-2"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              {{ t('nav.logout') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile Navigation Menu -->
    <div class="lg:hidden border-t border-gray-200 bg-gray-50 px-4 py-2 overflow-x-auto">
      <div class="flex gap-2 pb-2 min-w-min">
        <RouterLink
          v-for="item in navItems"
          :key="item.path"
          :to="item.path"
          class="px-3 py-2 text-xs font-medium rounded-lg transition-colors duration-200 flex-shrink-0 whitespace-nowrap"
          :class="isActive(item.path)
            ? 'text-primary-600 bg-primary-50'
            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
          "
        >
          {{ item.label }}
        </RouterLink>
      </div>
    </div>
  </nav>

  <!-- Profile Update Modal -->
  <ProfileUpdateModal
    :is-open="showProfileUpdateModal"
    @close="showProfileUpdateModal = false"
  />

  <!-- Change Password Modal -->
  <ChangePasswordModal
    :is-open="showChangePasswordModal"
    @close="showChangePasswordModal = false"
  />

  <!-- Passkey Manager Modal -->
  <PasskeyManagerModal
    :is-open="showPasskeyManagerModal"
    @close="showPasskeyManagerModal = false"
  />
</template>

<style scoped></style>
